import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { CvDto, UserAccountDto } from 'src/app/services/api-client/models';
import { CvRestControllerService, UserAccountRestControllerService } from 'src/app/services/api-client/services';
import { LanguageEnum } from 'src/app/shared/enums/language.model';
import { FormControl, FormGroup } from '@angular/forms';
import { CvService } from '../services/cv.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { CreateCvService } from 'src/app/shared/services/create-cv.service';
import { switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDeletionPopupComponent } from 'src/app/shared/components/confirm-deletion-popup/confirm-deletion-popup.component';

@Component({
  selector: 'app-cv-list',
  templateUrl: './cv-list.component.html',
  styleUrls: ['./cv-list.component.scss']
})
export class CvListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() public cvName: string;
  @Output() public language: LanguageEnum;
  @Output() public cv = new EventEmitter<CvDto>();
  @Input() cvList: CvDto[] = [];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('tableWrapper') tableWrapper: ElementRef;
  dataSource = new MatTableDataSource<CvDto>();
  cvListCurrent: CvDto[] = [];
  newCvForm: FormGroup;
  isNew = false;
  user: any = '';
  email = '';
  currentUser = true;
  userId: number;
  displayedColumns = ['name', 'language', 'createdDate', 'actions'];
  subscriptions = new Subscription();
  pageSize = 10;

  constructor(
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private cvRestControllerService: CvRestControllerService,
    private cvService: CvService,
    private toastrService: ToastrService,
    private userAccountService: UserAccountRestControllerService,
    private createCvService: CreateCvService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.userId = parseInt(this.activatedRoute.snapshot.paramMap.get('userId'));
    if (Number.isNaN(this.userId)) this.userId = null;
    if (this.userId) {
      this.cvList = this.activatedRoute.snapshot.data['cvList'];
      const sub = this.userAccountService.getUserAccountById({id: this.userId})
        .subscribe((user: UserAccountDto) => {
          this.currentUser = false;
          if (user.firstName != null && user.lastName != null) {
            this.user = user.firstName + ' ' + user.lastName;
          }
          this.email = user.email;
        });
      this.subscriptions.add(sub);
    }
    this.newCvForm = new FormGroup({
      name: new FormControl(''),
      language: new FormControl()
    })
    this.cvList = this.activatedRoute.snapshot.data['cvList'];
    if (!this.cvList) {
      const sub = this.cvRestControllerService.getCvsByType({cvType: 'CONSULTANT_CV'}).subscribe(
        data => {
          this.cvList = data;
          this.convert();
        }, (e) => {
          console.log('Error! No CVs found!', e);
        });
      this.subscriptions.add(sub);
    } else {
      this.convert();
    }
  }

  private configureTableWrapper(isAdd: boolean) {
    if (isAdd) {
      this.tableWrapper?.nativeElement.classList.add('table-wrapper');
    } else {
      this.tableWrapper?.nativeElement.classList.remove('table-wrapper');
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.sort.disableClear = true;
    this.sort.start = 'desc';
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'createdDate': return new Date(item.createdDate);
        default: return item[property];
      }
    };
    this.dataSource.sort.sort({id: 'createdDate', start: 'desc', disableClear: true});
    this.cdr.detectChanges();
  }

  goToCv(id: number, cvLang: string) {
    let lang = (cvLang === 'Englisch') ? 'en' : 'de';
    let url = 'consultant-cv/' + lang + '/' + id;
    window.open(url, '_self');
  }

  downloadDocX(cv: CvDto): void {
    this.cvService.downloadDocX(cv);
  }

  isConfirmed(): boolean {
    let warnMsg: string;
    this.subscriptions.add(this.translate.get('ask_cv_delete').subscribe(txt => warnMsg = txt));
    return confirm(warnMsg);
  }

  removeCV(cvId: number, cvName: string) {
    let warnMsg: string;
    this.subscriptions.add(this.translate.get('ask_cv_delete').subscribe(txt => warnMsg = txt));
    this.dialog.open(ConfirmDeletionPopupComponent, {
      width: '370px',
      disableClose: true,
      autoFocus: false,
      position: {top: '20px'},
      data: {
        message: warnMsg
      }
    }).afterClosed().subscribe(isConfirmed => {
      if (isConfirmed) {
        const sub = this.cvRestControllerService.deleteCv({id: cvId}).subscribe(() => {
          this.cvList.forEach((cv: CvDto, index: number) => {
            if (cv.id == cvId) this.cvList.splice(index, 1);
          })
          this.dataSource.data = this.cvList;
          this.configureTableWrapper(this.cvList.length > this.pageSize);
          this.subscriptions.add(this.translate.get('cv_delete_success').subscribe(txt => this.toastrService.success(txt, cvName)));
        }, (e) => {
          this.subscriptions.add(this.translate.get('cv_delete_failed').subscribe(txt => this.toastrService.error(txt, cvName)));
          console.log('Error! Failed to remove CV', e);
        });
        this.subscriptions.add(sub);
      }
    });
  }

  createNewVersion(cvId: number) {
    this.cvRestControllerService.versionConsultantCv({id: cvId}).subscribe(res => {
      this.cvList.push(res);
      this.convert();
    });
  }

  convert() {
    this.cvList.forEach((cv: any) => {
      cv.language = (cv.language != 'ENGLISH') ? 'Deutsch' : 'Englisch';
    });
    this.dataSource.data = this.cvList;
    this.configureTableWrapper(this.cvList.length > this.pageSize);
  }

  openCreateSidebar() {
    this.createCvService.openCreateSideBar();
    const sub = this.createCvService.cvData.pipe(
      switchMap(data => {
        this.cvName = data.name;
        this.language = data.language;
        return this.cvRestControllerService.createConsultantCv({
          userID: this.userId,
          name: this.cvName,
          language: this.language
        })
      })
    ).subscribe(cv => {
      this.createCvService.closeCreateSideBar();
      this.subscriptions.add(this.translate.get('cv_create_success').subscribe(txt => this.toastrService.success(txt, this.cvName)));
      if (!this.cvList) this.cvList = [];
      this.cvList.push(cv);
      this.convert();
      sub.unsubscribe();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
